import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Sparkles, Dumbbell, Utensils, Target, RefreshCw, ChevronDown, ChevronUp,
  Clock, Flame, Droplets, Loader2, Calendar, AlertTriangle
} from 'lucide-react';
import FeatureDisclaimer from './FeatureDisclaimer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MyPlan = ({ token }) => {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [expandedDay, setExpandedDay] = useState(null);
  const [expandedMeal, setExpandedMeal] = useState(null);
  const [activeTab, setActiveTab] = useState('workout');

  useEffect(() => {
    fetchPlan();
  }, []);

  const fetchPlan = async () => {
    try {
      const res = await axios.get(`${API}/plan/latest`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data?.plan) {
        setPlan(res.data);
      }
    } catch (err) {
      console.error('Failed to fetch plan:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const res = await axios.post(`${API}/plan/generate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlan(res.data);
    } catch (err) {
      console.error('Failed to regenerate plan:', err);
    } finally {
      setRegenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={32} className="text-[#FF6600] animate-spin" />
      </div>
    );
  }

  if (!plan?.plan) {
    return (
      <div className="max-w-lg mx-auto text-center py-12" data-testid="my-plan-empty">
        <div className="w-20 h-20 bg-gradient-to-r from-[#FF6600] to-[#FF8533] rounded-2xl flex items-center justify-center mx-auto mb-6">
          <Sparkles size={36} className="text-white" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">No Plan Yet</h2>
        <p className="text-gray-500 dark:text-gray-400 mb-6 text-sm">
          Generate a personalized workout and meal plan based on your profile, fitness goals, and dietary preferences.
        </p>
        <button
          onClick={handleRegenerate}
          disabled={regenerating}
          className="px-8 py-3.5 bg-gradient-to-r from-[#FF6600] to-[#FF8533] text-white font-bold rounded-xl hover:shadow-lg transition-all flex items-center gap-2 mx-auto disabled:opacity-50"
          data-testid="generate-first-plan-btn"
        >
          {regenerating ? (
            <><Loader2 size={18} className="animate-spin" /> Generating...</>
          ) : (
            <><Sparkles size={18} /> Generate My Plan</>
          )}
        </button>
      </div>
    );
  }

  const { plan: planData, created_at } = plan;
  const createdDate = created_at ? new Date(created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';

  return (
    <div className="max-w-4xl mx-auto pb-8" data-testid="my-plan-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Sparkles size={22} className="text-[#FF6600]" />
            My Plan
          </h1>
          {createdDate && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Generated {createdDate}</p>
          )}
        </div>
        <button
          onClick={handleRegenerate}
          disabled={regenerating}
          className="flex items-center gap-2 px-4 py-2 bg-[#FF6600] text-white rounded-xl text-sm font-semibold hover:bg-[#E55C00] transition-colors disabled:opacity-50"
          data-testid="regenerate-plan-btn"
        >
          {regenerating ? (
            <><Loader2 size={14} className="animate-spin" /> Regenerating...</>
          ) : (
            <><RefreshCw size={14} /> Regenerate</>
          )}
        </button>
      </div>

      {/* Disclaimer */}
      <div className="mb-4">
        <FeatureDisclaimer type="general" />
      </div>

      {/* Macros Card */}
      {planData.daily_macros && (
        <div className="bg-gradient-to-r from-[#FF6600] to-[#FF8533] rounded-2xl p-5 mb-5 text-white" data-testid="plan-macros">
          <h3 className="font-bold text-sm mb-3 opacity-90">Daily Targets</h3>
          <div className="grid grid-cols-5 gap-3 text-center">
            <div>
              <Flame size={16} className="mx-auto mb-1 opacity-80" />
              <div className="text-xl font-bold">{planData.daily_macros.calories}</div>
              <div className="text-xs opacity-75">Calories</div>
            </div>
            <div>
              <div className="text-xl font-bold">{planData.daily_macros.protein_g}g</div>
              <div className="text-xs opacity-75">Protein</div>
            </div>
            <div>
              <div className="text-xl font-bold">{planData.daily_macros.carbs_g}g</div>
              <div className="text-xs opacity-75">Carbs</div>
            </div>
            <div>
              <div className="text-xl font-bold">{planData.daily_macros.fats_g}g</div>
              <div className="text-xs opacity-75">Fats</div>
            </div>
            <div>
              <Droplets size={16} className="mx-auto mb-1 opacity-80" />
              <div className="text-xl font-bold">{planData.daily_macros.water_liters || '-'}L</div>
              <div className="text-xs opacity-75">Water</div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setActiveTab('workout')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all ${
            activeTab === 'workout'
              ? 'bg-[#FF6600] text-white'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
          }`}
          data-testid="tab-workout"
        >
          <Dumbbell size={16} /> Workout Plan
        </button>
        <button
          onClick={() => setActiveTab('meals')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all ${
            activeTab === 'meals'
              ? 'bg-[#FF6600] text-white'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
          }`}
          data-testid="tab-meals"
        >
          <Utensils size={16} /> Meal Plan
        </button>
      </div>

      {/* Workout Tab */}
      {activeTab === 'workout' && planData.weekly_workout_plan && (
        <div className="space-y-3" data-testid="workout-plan">
          {Object.entries(planData.weekly_workout_plan).map(([dayKey, day]) => (
            <div key={dayKey} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <button
                onClick={() => setExpandedDay(expandedDay === dayKey ? null : dayKey)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 dark:bg-gray-900 dark:hover:bg-gray-750 transition-colors"
                data-testid={`day-${dayKey}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
                    <Calendar size={16} className="text-[#FF6600]" />
                  </div>
                  <div>
                    <span className="font-bold text-sm text-gray-900 dark:text-white capitalize">{dayKey.replace('_', ' ')}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 block">{day.name || day.focus}</span>
                  </div>
                </div>
                {expandedDay === dayKey ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
              </button>
              {expandedDay === dayKey && day.exercises && (
                <div className="px-4 pb-4 space-y-2 border-t border-gray-100 dark:border-gray-700 pt-3">
                  {day.exercises.map((exercise, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b border-gray-50 dark:border-gray-700 last:border-0">
                      <div>
                        <span className="text-sm font-medium text-gray-800 dark:text-gray-100 dark:text-gray-200">{exercise.name}</span>
                        {exercise.notes && <span className="text-xs text-gray-400 block">{exercise.notes}</span>}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                        <span className="bg-orange-50 dark:bg-orange-900/20 text-[#FF6600] px-2 py-0.5 rounded font-semibold">
                          {exercise.sets} x {exercise.reps}
                        </span>
                        {exercise.rest && (
                          <span className="flex items-center gap-1">
                            <Clock size={12} /> {exercise.rest}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Meals Tab */}
      {activeTab === 'meals' && planData.daily_meal_plan && (
        <div className="space-y-3" data-testid="meal-plan">
          {Object.entries(planData.daily_meal_plan).map(([mealKey, meal]) => (
            <div key={mealKey} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <button
                onClick={() => setExpandedMeal(expandedMeal === mealKey ? null : mealKey)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 dark:bg-gray-900 dark:hover:bg-gray-750 transition-colors"
                data-testid={`meal-${mealKey}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                    <Utensils size={16} className="text-green-600" />
                  </div>
                  <div>
                    <span className="font-bold text-sm text-gray-900 dark:text-white">{meal.name}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 block">{meal.time} &middot; {meal.calories} cal</span>
                  </div>
                </div>
                {expandedMeal === mealKey ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
              </button>
              {expandedMeal === mealKey && (
                <div className="px-4 pb-4 border-t border-gray-100 dark:border-gray-700 pt-3">
                  {meal.foods && (
                    <ul className="space-y-1.5 mb-3">
                      {meal.foods.map((food, i) => (
                        <li key={i} className="text-sm text-gray-600 dark:text-gray-300 flex items-center gap-2">
                          <span className="w-1.5 h-1.5 bg-green-500 rounded-full flex-shrink-0"></span>
                          {food}
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="flex gap-4 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 rounded-lg p-2.5">
                    <span>Protein: <strong className="text-blue-600">{meal.protein}g</strong></span>
                    <span>Carbs: <strong className="text-green-600">{meal.carbs}g</strong></span>
                    <span>Fats: <strong className="text-yellow-600">{meal.fats}g</strong></span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Tips */}
      {planData.tips && planData.tips.length > 0 && (
        <div className="mt-5 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-xl p-4" data-testid="plan-tips">
          <h3 className="font-bold text-sm text-blue-800 dark:text-blue-300 mb-2 flex items-center gap-2">
            <Target size={14} /> Pro Tips
          </h3>
          <ul className="space-y-1.5">
            {planData.tips.map((tip, i) => (
              <li key={i} className="text-xs text-blue-700 dark:text-blue-300 flex items-start gap-2">
                <span className="text-blue-500 mt-0.5 flex-shrink-0">&#x2022;</span>
                {tip}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Progression */}
      {planData.weekly_progression && (
        <div className="mt-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-xl p-4">
          <h3 className="font-bold text-sm text-purple-800 dark:text-purple-300 mb-2">Weekly Progression</h3>
          <p className="text-xs text-purple-700 dark:text-purple-300">{planData.weekly_progression}</p>
        </div>
      )}
    </div>
  );
};

export default MyPlan;
